/* eslint-disable @typescript-eslint/no-explicit-any */
import { User, UserOnboardingState } from '51trips-frontend-core'
import { DocumentData, DocumentSnapshot } from 'firebase/firestore'

import userOnboardingStateConverter from './UserOnboardingStateConverter'

const userConverter = {
  toFirestore: (user: User) => {
    return {
      isAdmin: user.isAdmin,
      email: user.email,
      fullName: user.fullName,
      profileImageURL: user.profileImageURL,
      tripsIDs: user.tripsIDs,
      visitedCountriesIDs: user.visitedCountriesIDs,
      wantToVisitCountriesIDs: user.wantToVisitCountriesIDs,
      visitedWondersIDs: user.visitedWondersIDs,
      accomplishedBucketListItemsIDs: user.accomplishedBucketListItemsIDs,
      messagingTokens: user.messagingTokens,
      installationsIDs: user.installationsIDs,
      numberOfBucketListItems: user.numberOfBucketListItems,
      isPrivacyPolicyAccepted: user.isPrivacyPolicyAccepted,
      isCommercialConsentAccepted: user.isCommercialConsentAccepted,
      onboardingState: user.onboardingState,
      deviceLanguageTag: user.deviceLanguageTag
    }
  },
  fromFirestore: (snapshot: DocumentSnapshot<DocumentData, DocumentData>) => {
    const data = snapshot.data()
    if (data) {
      let isAdmin: boolean = false
      if (typeof data.isAdmin === 'boolean') {
        isAdmin = data.isAdmin
      }

      let fullName: string = ''
      if (typeof data.fullName === 'string') {
        fullName = data.fullName
      }

      let email: string = ''
      if (typeof data.email === 'string') {
        email = data.email
      }

      let profileImageURL: string | undefined
      if (typeof data.profileImageURL === 'string') {
        profileImageURL = data.profileImageURL
      }

      let tripsIDs: string[] = []
      if (
        Array.isArray(data?.tripsIDs) &&
        data.tripsIDs.length &&
        data.tripsIDs.every((tripID: any) => typeof tripID === 'string')
      ) {
        tripsIDs = data.tripsIDs
      }

      let visitedCountriesIDs: string[] = []
      if (
        Array.isArray(data?.visitedCountriesIDs) &&
        data.visitedCountriesIDs.length &&
        data.visitedCountriesIDs.every((visitedCountry: any) => typeof visitedCountry === 'string')
      ) {
        visitedCountriesIDs = data.visitedCountriesIDs
      }

      let wantToVisitCountriesIDs: string[] = []
      if (
        Array.isArray(data?.wantToVisitCountriesIDs) &&
        data.wantToVisitCountriesIDs.length &&
        data.wantToVisitCountriesIDs.every(
          (wantToVisitCountry: any) => typeof wantToVisitCountry === 'string'
        )
      ) {
        wantToVisitCountriesIDs = data.wantToVisitCountriesIDs
      }

      let visitedWondersIDs: string[] = []
      if (
        Array.isArray(data?.visitedWondersIDs) &&
        data.visitedWondersIDs.length &&
        data.visitedWondersIDs.every((visitedWonder: any) => typeof visitedWonder === 'string')
      ) {
        visitedWondersIDs = data.visitedWondersIDs
      }

      let wantToVisitWondersIDs: string[] = []
      if (
        Array.isArray(data?.wantToVisitWondersIDs) &&
        data.wantToVisitWondersIDs.length &&
        data.wantToVisitWondersIDs.every(
          (wantToVisitWonder: any) => typeof wantToVisitWonder === 'string'
        )
      ) {
        wantToVisitWondersIDs = data.wantToVisitWondersIDs
      }

      let accomplishedBucketListItemsIDs: string[] = []
      if (
        Array.isArray(data?.accomplishedBucketListItemsIDs) &&
        data.accomplishedBucketListItemsIDs &&
        data.accomplishedBucketListItemsIDs.length &&
        data.accomplishedBucketListItemsIDs.every(
          (accomplishedBucketListItemID: any) => typeof accomplishedBucketListItemID === 'string'
        )
      ) {
        accomplishedBucketListItemsIDs = data.accomplishedBucketListItemsIDs
      }

      let messagingTokens: string[] = []
      if (
        Array.isArray(data?.messagingTokens) &&
        data.messagingTokens.length &&
        data.messagingTokens.every((messagingToken: any) => typeof messagingToken === 'string')
      ) {
        messagingTokens = data.messagingTokens
      }

      let installationsIDs: string[] = []
      if (
        Array.isArray(data?.installationsIDs) &&
        data.installationsIDs.length &&
        data.installationsIDs.every((installationID: any) => typeof installationID === 'string')
      ) {
        installationsIDs = data.installationsIDs
      }

      let numberOfBucketListItems: number = 0
      if (typeof data.numberOfBucketListItems === 'number') {
        numberOfBucketListItems = data.numberOfBucketListItems
      }

      let isPrivacyPolicyAccepted: boolean = false
      if (typeof data.isPrivacyPolicyAccepted === 'boolean') {
        isPrivacyPolicyAccepted = data.isPrivacyPolicyAccepted
      }

      let isCommercialConsentAccepted: boolean = false
      if (typeof data.isCommercialConsentAccepted === 'boolean') {
        isCommercialConsentAccepted = data.isCommercialConsentAccepted
      }

      let onboardingState: UserOnboardingState | undefined
      if (data.onboardingState) {
        const userOnboardingState = userOnboardingStateConverter.fromFirestore(data.onboardingState)
        if (userOnboardingState) {
          onboardingState = userOnboardingState
        }
      }

      let deviceLanguageTag: string | undefined
      if (typeof data.deviceLanguageTag === 'string') {
        deviceLanguageTag = data.deviceLanguageTag
      }

      return new User(
        snapshot.id,
        isAdmin,
        fullName,
        email,
        profileImageURL,
        tripsIDs,
        visitedCountriesIDs,
        wantToVisitCountriesIDs,
        visitedWondersIDs,
        wantToVisitWondersIDs,
        accomplishedBucketListItemsIDs,
        messagingTokens,
        installationsIDs,
        numberOfBucketListItems,
        isPrivacyPolicyAccepted,
        isCommercialConsentAccepted,
        onboardingState,
        deviceLanguageTag
      )
    } else {
      return undefined
    }
  }
}

export default userConverter
